import React, { useEffect, useState } from "react";
import { 
  Button, 
  CircularProgress, 
  Switch, 
  FormControlLabel, 
  Alert, 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  FormHelperText,
  Typography,
  Divider,
  Paper,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import LabelInput from "../../../components/labelInput/LabelInput";
import OrpenService from "../../../core/http/service/OrpenService";
import { ConfigContainer } from "../OrpenConfig.styled";
import useResponsiveInfo from "../../../hooks/useResponsiveInfo";
import moment from "moment";
import Colors from "../../../constants/Colors";

interface Props {
  empresaId: string;
  isViewState?: boolean;
}

const OrpenGeneralConfig = ({ empresaId, isViewState = false }: Props) => {
  const { isMobile } = useResponsiveInfo();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "success" as "success" | "error" });
  const [templates, setTemplates] = useState<any[]>([]);
  const [previewData, setPreviewData] = useState<any>(null);
  const [config, setConfig] = useState<any>({
    empresaId,
    orpenApiUrl: "",
    spmBaseUrl: "",
    token: "",
    entrance: "",
    agent: "",
    actionAux: "",
    leadTimeHours: 24,
    cancelLeadTimeHours: 24,
    ativo: true,
    agendamentoTemplateId: null,
  });

  useEffect(() => {
    fetchData();
  }, [empresaId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response: any = await OrpenService.getConfiguracao(empresaId);
      if (response.data && response.data.data) {
        const configData = response.data.data;
        setConfig(configData);
        
        if (configData.id) {
          const templatesResp: any = await OrpenService.getTemplates(configData.id);
          setTemplates(templatesResp.data.data || []);
        }

        fetchPreview();
      }
    } catch (error) {
      console.error("Error fetching Orpen config", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async () => {
    setLoadingPreview(true);
    try {
      const response: any = await OrpenService.getPreview(empresaId);
      setPreviewData(response.data.data);
    } catch (error) {
      console.error("Error fetching preview", error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ text: "", type: "success" });
    try {
      await OrpenService.upsertConfiguracao(config);
      setMessage({ text: "Configuração salva com sucesso!", type: "success" });
      fetchPreview();
    } catch (error) {
      setMessage({ text: "Erro ao salvar configuração.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleProcessar = async () => {
    setProcessing(true);
    setMessage({ text: "", type: "success" });
    try {
      await OrpenService.processar(empresaId);
      setMessage({ text: "Processamento iniciado com sucesso!", type: "success" });
      setTimeout(fetchPreview, 2000);
    } catch (error) {
      setMessage({ text: "Erro ao iniciar processamento.", type: "error" });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ ...message, text: "" })}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Parâmetros de API
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Orpen API URL"
                        value={config.orpenApiUrl}
                        onChangeText={(e: any) => setConfig({ ...config, orpenApiUrl: e.target.value })}
                        placeholder="https://empresa.orpen.com.br"
                    />
                </Grid>
                <Grid item xs={12}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="SPM Base URL (Callback)"
                        value={config.spmBaseUrl}
                        onChangeText={(e: any) => setConfig({ ...config, spmBaseUrl: e.target.value })}
                        placeholder="https://spm.hmv.org.br"
                    />
                </Grid>
                <Grid item xs={12}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Token"
                        value={config.token}
                        onChangeText={(e: any) => setConfig({ ...config, token: e.target.value })}
                        placeholder="Basic token..."
                    />
                </Grid>
                <Grid item xs={12}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Número de Origem (Entrance)"
                        value={config.entrance}
                        onChangeText={(e: any) => setConfig({ ...config, entrance: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="ID do Agente (Agent)"
                        value={config.agent}
                        onChangeText={(e: any) => setConfig({ ...config, agent: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="ID de Tabulação (Action Aux)"
                        value={config.actionAux}
                        onChangeText={(e: any) => setConfig({ ...config, actionAux: e.target.value })}
                    />
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Regras de Envio
              </Typography>

              <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Antecedência para envio (Horas)"
                        type="number"
                        value={config.leadTimeHours}
                        onChangeText={(e: any) => setConfig({ ...config, leadTimeHours: parseInt(e.target.value) || 0 })}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Antecedência para cancelamento (Horas)"
                        type="number"
                        value={config.cancelLeadTimeHours}
                        onChangeText={(e: any) => setConfig({ ...config, cancelLeadTimeHours: parseInt(e.target.value) || 0 })}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControl variant="standard" fullWidth disabled={isViewState}>
                        <InputLabel id="template-agendamento-label">Template Padrão</InputLabel>
                        <Select
                            labelId="template-agendamento-label"
                            value={config.agendamentoTemplateId || ""}
                            disabled={isViewState}
                            onChange={(e) => setConfig({ ...config, agendamentoTemplateId: e.target.value || null })}
                        >
                            <MenuItem value=""><em>Automático (Primeiro ativo)</em></MenuItem>
                            {templates.map((t) => (
                                <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>Template usado para confirmações automáticas.</FormHelperText>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={config.ativo}
                                disabled={isViewState}
                                onChange={(e) => setConfig({ ...config, ativo: e.target.checked })}
                                color="primary"
                            />
                        }
                        label={config.ativo ? "Integração Habilitada" : "Integração Desabilitada"}
                    />
                </Grid>
              </Grid>

              {!isViewState && (
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                      variant="contained" 
                      size="large"
                      startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} 
                      onClick={handleSave} 
                      disabled={saving}
                      sx={{ borderRadius: 2, px: 4 }}
                  >
                      {saving ? "Salvando..." : "Salvar Alterações"}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', bgcolor: '#fbfcfe' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Resumo de Envios
                </Typography>
                <Tooltip title="Atualizar resumo">
                    <IconButton onClick={fetchPreview} disabled={loadingPreview} size="small">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
              </Box>

              {loadingPreview ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                    <CircularProgress size={30} sx={{ mb: 2 }} />
                    <Typography variant="body2" color="textSecondary">Calculando fila...</Typography>
                </Box>
              ) : previewData ? (
                <Box>
                    <Paper elevation={0} sx={{ p: 2, mb: 3, textAlign: 'center', borderRadius: 2, bgcolor: Colors.SPM50, border: `1px solid ${Colors.SPM100}` }}>
                        <Typography variant="h3" sx={{ color: Colors.SPM600, fontWeight: 'bold' }}>
                            {previewData.totalPendentes}
                        </Typography>
                        <Typography variant="overline" sx={{ color: Colors.SPM600 }}>
                            Agendamentos Pendentes
                        </Typography>
                    </Paper>

                    <Typography variant="caption" display="block" color="textSecondary" gutterBottom>
                        JANELA DE FILTRO:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {moment(previewData.dataInicioFiltro).format("DD/MM [às] HH:mm")} até {moment(previewData.dataFimFiltro).format("DD/MM [às] HH:mm")}
                    </Typography>

                    <Alert severity="info" icon={false} sx={{ mb: 3, '& .MuiAlert-message': { width: '100%' } }}>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                            Com base no lead time de <strong>{config.leadTimeHours}h</strong>, o sistema buscará agendamentos que ainda não foram notificados.
                        </Typography>
                    </Alert>

                    {!isViewState && (
                      <Button 
                          fullWidth
                          variant="contained" 
                          color="secondary"
                          size="large"
                          startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                          onClick={handleProcessar}
                          disabled={processing || previewData.totalPendentes === 0}
                          sx={{ borderRadius: 2 }}
                      >
                          {processing ? "Processando..." : "Disparar Agora"}
                      </Button>
                    )}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 4 }}>
                    Dados de resumo indisponíveis.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrpenGeneralConfig;
